import { createColumnHelper, Row } from '@tanstack/react-table';
import CommonTable from 'components/table/CommonTable';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import {
  IntegrationCategory,
  IntegrationStatus,
  IntegrationType,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import { useTranslation } from 'react-i18next';
import { API_MAX_LIMIT } from 'services/API.constants';
import { useNavigate } from 'react-router-dom';
import useFetchAgency from 'components/domain/agency/useFetchAgency';
import useAppUser from 'hooks/useAppUser';
import TabHeader from '../components/tabHeader/TabHeader';
import ChannelCell from '../directChannels/channelsTable/cells/channelCell/ChannelCell';
import StatusCell from '../directChannels/channelsTable/cells/statusCell/StatusCell';
import { integrationsSettingsQuery } from '../integrationSettingsQuery';
import AgencyConfirmationWarning from '../components/agencyConfirmationWarning/AgencyConfirmationWarning';
import { ChannelsTableWrapper } from '../directChannels/channelsTable/ChannelsTable.styles';

const ChannelManagersTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agencyUid } = useAppUser();
  const { data: agency, isLoading: isLoadingAgency } = useFetchAgency({
    agencyUid: agencyUid as string,
  });

  const isAgencyConfirmed = agency?.agencyConfirmed ?? false;

  const columnHelper = createColumnHelper<InternalIntegrationSetting>();
  const columns = [
    columnHelper.accessor('integration.name', {
      id: 'name',
      header: t('pageChannels.table.columns.channel'),
      enableSorting: true,
      cell: ChannelCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('integrationStats.connectedAccounts', {
      header: t('pageChannels.table.columns.connectedAccounts'),
      enableSorting: false,
    }),
    columnHelper.accessor('integrationStats.connectedProperties', {
      header: t('pageChannels.table.columns.connectedProperties'),
      enableSorting: false,
      cell: ({ getValue }) => (getValue() === 0 ? '-' : getValue()),
    }),
    columnHelper.accessor('agencySettings.status', {
      id: 'actions',
      header: t('pageChannels.table.columns.status'),
      cell: StatusCell,
      enableSorting: true,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { isLoading: isLoadingTable, tableInstance } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      integrationsSettingsQuery({
        sorting: options.sorting,
        type: IntegrationType.CHANNEL,
        category: IntegrationCategory.CHANNEL_MANAGER,
      }),
    // The query returns all channels without pagination
    pageSize: API_MAX_LIMIT,
  });

  const displayAgencyConfirmationWarning =
    !isLoadingAgency && !isAgencyConfirmed;

  const redirectToDetails = (
    _: React.MouseEvent<HTMLTableRowElement>,
    row: Row<InternalIntegrationSetting>,
  ) => {
    const { enumId } = row.original.integration;
    const { status } = row.original.agencySettings;
    const isActive = status === IntegrationStatus.ACTIVE;

    if (isActive) {
      navigate(`/channels/details/${enumId}`);
    }
  };

  return (
    <section>
      <TabHeader title={t('pageChannels.channelManagers.title')} />

      {displayAgencyConfirmationWarning && <AgencyConfirmationWarning />}

      <ChannelsTableWrapper>
        <CommonTable
          tableInstance={tableInstance}
          isLoading={isLoadingTable}
          data-testid="channel-managers-table"
          handleRowClick={isAgencyConfirmed ? redirectToDetails : undefined}
        />
      </ChannelsTableWrapper>
    </section>
  );
};

export default ChannelManagersTab;
